import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo
} from "react";
import { useHistory } from "react-router-dom";

import { Form } from "@unform/web";
import * as Yup from "yup";
import getValidationErrors from "../../../../utils/getValidationErrors";
import { validateDocument } from "../../../../utils/documentValidation";
import {
  buildCredentialName,
  allowCredentialNameToReceiveValue,
} from "../../../../utils/credentialName";

import { useSelector } from "react-redux";

import { FormattedMessage, useIntl } from "react-intl";

import {
  Container,
  TitleContainer,
  Title,
  Session,
  FieldsContainer,
  ActionContainer,
  BadgeContainer,
} from "./styles";

import CpfCnpj from "../../../../components/CpfCnpj";

import { useToast } from "../../../../hooks/toast";
import { validateSingleFieldOnBlur } from "../../../../utils/formValidations";
import api from "../../../../service";
import { getCountries } from "react-phone-number-input/input";
import countryListEn from "react-phone-number-input/locale/en";
import countryListPt from "react-phone-number-input/locale/pt";
import AnimatedMainContainer from "../../../../components/AnimatedMainContainer";
import InputWithHorizontalLabel from "../../../../components/InputWithHorizontalLabel";
import SelectWithLabel from "../../../../components/SelectWithLabel";
import Badge from "../../../../components/Badge";
import { useLoading } from "../../../../hooks/loading";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import Toggle from "../../../../components/Toggle";
import
  TelephoneInputWithLabel,
  { getFullTelephoneNumber }
from "../../../../components/TelephoneInputWithLabel";
import {
  getBrazilianStates,
  getBrazilianCitiesByStateId,
} from "../../../../service/localities.service";

const AdminAssociateCollaboratorsCreate = () => {
  const formRef = useRef(null);
  const intl = useIntl();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);
  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState();
  const [personType, setPersonType] = useState("F");
  const [documentLabel, setDocumentLabel] = useState(
    intl.formatMessage({ id: "document_cpf" })
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFastTrack, setIsFastTrack] = useState(false);
  const [isCreateCredential, setIsCreateCredential] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState();
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState();
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [selectedRoleType, setSelectedRoleType] = useState();
  const [countries, setCountries] = useState([]);
  const [brazilianStates, setBrazilianStates] = useState([]);
  const [brazilianCities, setBrazilianCities] = useState([]);
  const [canApplyCredentialName, setCanApplyCredentialName] = useState(true);
  const isPortuguese = useMemo(()=> intl.locale === 'pt', [intl]);

  const history = useHistory();

  const roleTypes = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "exhibitor" }),
        value: "EXHIBITOR",
      },
      {
        label: intl.formatMessage({ id: "sponsor_without_m2" }),
        value: "SPONSOR_WITHOUT_M2",
      },
      {
        label: intl.formatMessage({ id: "service_provider" }),
        value: "SERVICE_PROVIDER",
      },
    ],
    [intl]
  );

  const schema = useMemo(() => Yup.object().shape({
    roleType: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    user: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    nationality: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    name: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    last_name: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    document: Yup.string()
      .required(intl.formatMessage({ id: "error.field_required" }))
      .test(
        "document-validation",
        "",
        function (value) {
          if (!value) return true;

          const formData = this.parent;
          const nationalityField = formData.nationality;

          let isInternational = false;
        
          if (typeof nationalityField === 'object' && nationalityField !== null) {
            isInternational = nationalityField.value === true;
          } else if (typeof nationalityField === 'boolean') {
            isInternational = nationalityField;
          } else if (typeof nationalityField === 'string') {
            isInternational = nationalityField === 'true' || nationalityField === true;
          }

          // personType validation as fallback
          if (typeof isInternational === 'undefined') {
            isInternational = personType === "I";
          }
        
          const validation = validateDocument(value, isInternational, intl);
        
          if (!validation.isValid && validation.message) {
            return this.createError({ message: validation.message });
          }
        
          return true;
        }
      ),
    assignment: Yup.string()
      .max(50, intl.formatMessage({ id: "error.max_length" }, { length: 50 }))
      .required(intl.formatMessage({ id: "error.field_required" })),
    credential_name: Yup.string()
      .max(23, intl.formatMessage({ id: "error.max_length" }, { length: 23 }))
      .required(intl.formatMessage({ id: "error.field_required" })),
    email: Yup.string()
      .email(intl.formatMessage({ id: "error.valid_email" }))
      .nullable()
      .when("fast_track", {
        is: (fast_track) => !fast_track,
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
    country: Yup.string()
      .nullable()
      .when("nationality", {
        is: "true",
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          )
          .test(
            "not-empty",
            intl.formatMessage({ id: "error.field_required" }),
            (value) => value && value.trim().length > 0
          ),
      }),
    internationalCity: Yup.string()
      .nullable()
      .when("nationality", {
        is: "true",
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
    state: Yup.string()
      .nullable()
      .when("nationality", {
        is: "false",
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
    brazilianCity: Yup.string()
      .nullable()
      .when("nationality", {
        is: "false",
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
    telephone: Yup.string()
      .max(30, intl.formatMessage({ id: "error.max_length" }, { length: 30 }))
      .required(intl.formatMessage({ id: "error.field_required" })),
    event: Yup.string()
      .nullable()
      .when("create_credential", {
        is: (create_credential) => create_credential,
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
    opportunity: Yup.string()
      .nullable()
      .when("create_credential", {
        is: (create_credential) => create_credential,
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
    category: Yup.string()
      .nullable()
      .when("create_credential", {
        is: (create_credential) => create_credential,
        then: Yup.string()
          .nullable()
          .required(
            intl.formatMessage({
              id: "error.field_required",
            })
          ),
      }),
  }), [intl, personType]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        await schema.validate(data, { abortEarly: false });

        setLoading(true);
        if (loading) {
          return;
        }

        api
          .post(
            `/associate-collaborators/admin`,
            {
              fastTrack: isFastTrack,
              userId: data.user,
              international: data.nationality,
              name: data.name,
              lastName: data.last_name,
              credentialName: data.credential_name,
              document: data.document,
              jobTitle: data.assignment,
              email: data.email,
              country: data.country,
              city: data.nationality
                ? data.internationalCity
                : data.brazilianCity,
              state: data.state,
              telephone: getFullTelephoneNumber(
                data.telephone, data.telephone_selected_country),
              createCredential: data.create_credential,
              eventId: data.event,
              opportunityId: data.opportunity,
              credentialType: data.category,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            addToast({
              type: "success",
              title: intl.formatMessage({
                id: "success",
              }),
              description: intl.formatMessage({
                id: "success." + result.data.code,
              }),
            });
            setIsModalOpen(true);
          })
          .catch((err) =>
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            })
          )
          .finally(() => setLoading(false));
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [addToast, intl, loading, schema, token, isFastTrack]
  );

  useEffect(() => {
    formRef.current.setFieldValue("user");
    if (selectedRoleType) {
      handleLoading(true);
      api
        .get(`/users/active-clients-by-type`, {
          params: {
            role_type: selectedRoleType,
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((result) => {
          const users = result.data.users.map((user) => ({
            label: user.commercialName,
            value: user.id,
          }));
          setUsers(users);
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        })
        .finally(() => {
          handleLoading(false);
        });
    }
  }, [selectedRoleType, addToast, intl, token, handleLoading]);

  const validationBlur = useCallback((name) => {
    let errors = formRef.current.getErrors();

    validateSingleFieldOnBlur(errors, schema, name, formRef);
  }, [schema]);

  const handleTypeChanges = (value) => {
    formRef.current.setFieldValue("document", "");
    formRef.current.setFieldValue("country", "");
    formRef.current.setFieldValue("internationalCity", "");
    formRef.current.setFieldValue("state");
    formRef.current.setFieldValue("brazilianCity");
    setBrazilianCities([]);

    formRef.current.setErrors({});

    setTimeout(() => {
      const documentField = formRef.current.getFieldRef("document");
      if (documentField && documentField.current) {
        documentField.current.value = "";
      }
    }, 100);
    
    switch (value) {
      case false:
        setPersonType("F");
        setDocumentLabel(intl.formatMessage({ id: "document_cpf" }));
        break;
      case true:
        setPersonType("I");
        setDocumentLabel(
          intl.formatMessage({ id: "document_passport" })
        );
        break;
      default:
        setDocumentLabel(intl.formatMessage({ id: "document" }));
        break;
    }
  };

  const resetForm = () => {
    formRef.current.reset();
    formRef.current.setFieldValue("nationality", {
      label: intl.formatMessage({ id: "national" }),
      value: false,
    });
    formRef.current.setFieldValue("create_credential", false);
    formRef.current.setFieldValue("telephone_selected_country", {
      value: "BR"
    });
    setBrazilianCities([]);
    setIsCreateCredential(false);
    setIsModalOpen(false);
  };

  const handleRoleTypeChange = (type) => {
    setSelectedRoleType(type?.value);
    if (type?.value !== "SERVICE_PROVIDER") {
      setIsFastTrack(false);
    }
  };

  const handleBuildCredentialName = async () => {
    if (!canApplyCredentialName) return;

    const formData = formRef.current.getData();
    const { name, last_name } = formData;
    const credentialName = buildCredentialName(name, last_name);

    if (!credentialName.trim()) return;

    formRef.current.setFieldValue("credential_name", credentialName);
    try {
      await schema.validateAt("credential_name", {
        ...formRef.current.getData(),
        credential_name: credentialName,
      });

      formRef.current.setErrors({
        ...formRef.current.getErrors(),
        credential_name: undefined,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        formRef.current.setErrors({
          ...formRef.current.getErrors(),
          credential_name: error.errors[0],
        });
      }
    }
  };

  const handleCredentialNameChange = () => {
    const formData = formRef.current.getData();
    const { name, last_name, credential_name } = formData;

    setCanApplyCredentialName(
      allowCredentialNameToReceiveValue(name, last_name, credential_name));
  };

  const fetchBrazilianCities = async (stateId) => {
    try {
      handleLoading(true);
      const cities = await getBrazilianCitiesByStateId(stateId);
      setBrazilianCities(cities);
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "error.loading_cities",
        }),
      });
      setBrazilianCities([]);
    } finally {
      handleLoading(false);
    }
  };

  const handleStateChange = (state) => {
    formRef.current.setFieldValue("brazilianCity");

    if (!state?.stateId) {
      setBrazilianCities([]);
      return;
    }

    fetchBrazilianCities(state.stateId);
  };

  useEffect(() => {
    formRef.current.setFieldValue("event");
    if (isCreateCredential && selectedUser) {
      handleLoading(true);

      const config = {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      };

      api
        .get(`/admin/events/by-user/${selectedUser}`, config)
        .then((eventResponse) => {
          setEvents(eventResponse.data.output);
        })
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          })
        )
        .finally(() => handleLoading(false));
    }
  }, [
    addToast,
    handleLoading,
    isCreateCredential,
    selectedUser,
    intl,
    token,
  ]);

  useEffect(() => {
    formRef.current.setFieldValue("opportunity");
    if (selectedEvent && selectedUser) {
      handleLoading(true);

      const config = {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      };
      api
        .patch(
          `/exhibitor/opportunity/admin-accreditation/event`,
          { eventId: selectedEvent, userId: selectedUser },
          config
        )
        .then((result) =>
          setOpportunities(
            [...result.data.output].sort((a, b) =>
              a.label < b.label ? -1 : a.label > b.label ? 1 : 0
            )
          )
        )
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          })
        )
        .finally(() => handleLoading(false));
    }
  }, [
    addToast,
    intl,
    selectedEvent,
    selectedUser,
    token,
    handleLoading,
  ]);

  useEffect(() => {
    formRef.current.setFieldValue("category");
    if (selectedEvent && selectedOpportunity && selectedUser) {
      handleLoading(true);

      const config = {
        params: {
          event_id: selectedEvent,
          opportunity_id: selectedOpportunity,
          user_id: selectedUser,
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      };

      api
        .get(`/accreditation/quota`, config)
        .then((categoryResponse) => {
          const credentialCategories =
            categoryResponse.data.credentialCategoryOutputList.map((item) => ({
              label: isPortuguese ? item.title : item.englishTitle,
              value: item.credentialType,
            }));
          setCredentialTypes(credentialCategories);
        })
        .catch((error) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + error.response.data.code,
            }),
          })
        )
        .finally(() => handleLoading(false));
    }
  }, [
    addToast,
    intl,
    selectedEvent,
    selectedOpportunity,
    selectedUser,
    token,
    handleLoading,
    isPortuguese,
  ]);

  useEffect(() => {
    const fetchBrazilianStates = async () => {
      try {
        handleLoading(true);
        const states = await getBrazilianStates();
        setBrazilianStates(states);
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "error.loading_states",
          }),
        });
        setBrazilianStates([]);
      } finally {
        handleLoading(false);
      }
    };

    const countriesList = getCountries().map((countryCode) => ({
      value: isPortuguese ? countryListPt[countryCode] : countryListEn[countryCode],
      label: isPortuguese ? countryListPt[countryCode] : countryListEn[countryCode],
    }));
    
    const sortedCountries = countriesList.sort((a, b) => 
      a.label.localeCompare(b.label)
    );
    
    setCountries(sortedCountries);

    fetchBrazilianStates();
  }, [isPortuguese]);

  return (
    <AnimatedMainContainer>
      <Container>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={{
            nationality: {
              label: intl.formatMessage({ id: "national" }),
              value: false,
            },
          }}
        >
          <TitleContainer>
            <Title>
              <FormattedMessage id="new_collaborator" />
            </Title>
            <ActionContainer>
              <BadgeContainer
                onClick={() => history.go(-2)}
              >
                <Badge
                  color="#C9C9C9"
                  text={intl.formatMessage({ id: "goback" })}
                  fontSize="12px"
                />
              </BadgeContainer>
              <BadgeContainer onClick={() => formRef.current.submitForm()}>
                <Badge
                  color="#002244"
                  text={intl.formatMessage({ id: "save" })}
                  fontSize="12px"
                  loading={loading}
                />
              </BadgeContainer>
            </ActionContainer>
          </TitleContainer>
          <Session>
            <FieldsContainer>
              <SelectWithLabel
                name="roleType"
                validationBlur={validationBlur}
                label={intl.formatMessage({ id: "type" })}
                placeholder={intl.formatMessage({ id: "type" })}
                labelOrientation="horizontal"
                labelWidth="150px"
                options={roleTypes}
                onChange={handleRoleTypeChange}
                required
              />
              <SelectWithLabel
                name="user"
                validationBlur={validationBlur}
                label={intl.formatMessage({ id: "company" })}
                placeholder={intl.formatMessage({ id: "company" })}
                labelOrientation="horizontal"
                labelWidth="150px"
                options={users}
                onChange={(user) => setSelectedUser(user?.value)}
                required
              />
              {selectedRoleType === "SERVICE_PROVIDER" && (
                <Toggle
                  name="fast_track"
                  label={intl.formatMessage({
                    id: "fast_track",
                  })}
                  onClick={() => setIsFastTrack(!isFastTrack)}
                />
              )}
              <SelectWithLabel
                name="nationality"
                label={intl.formatMessage({ id: "nationality" })}
                placeholder={intl.formatMessage({ id: "nationality" })}
                labelOrientation="horizontal"
                labelWidth="150px"
                options={[
                  {
                    label: intl.formatMessage({ id: "national" }),
                    value: false,
                  },
                  {
                    label: intl.formatMessage({ id: "international" }),
                    value: true,
                  },
                ]}
                onChange={(e) => handleTypeChanges(e.value)}
                required
              />
              <InputWithHorizontalLabel
                name="name"
                placeholder={intl.formatMessage({
                  id: "name",
                })}
                label={intl.formatMessage({
                  id: "name",
                })}
                validationBlur={validationBlur}
                validateEnd={handleBuildCredentialName}
                labelWidth="150px"
                required
              />
              <InputWithHorizontalLabel
                name="last_name"
                placeholder={intl.formatMessage({
                  id: "last_name",
                })}
                label={intl.formatMessage({
                  id: "last_name",
                })}
                validationBlur={validationBlur}
                validateEnd={handleBuildCredentialName}
                labelWidth="150px"
                required
              />
              <InputWithHorizontalLabel
                name="credential_name"
                placeholder={intl.formatMessage({
                  id: "credential_name",
                })}
                label={intl.formatMessage({
                  id: "credential_name",
                })}
                description={intl.formatMessage(
                  {id: "up_to_custom_characters"},
                  {quantity: "23"},
                )}
                validationBlur={validationBlur}
                validateEnd={handleCredentialNameChange}
                maxLength="23"
                labelWidth="150px"
                required
              />
              <CpfCnpj
                name="document"
                placeholder={documentLabel}
                label={documentLabel}
                hasLabel
                labelOrientation="horizontal"
                personType={personType}
                validationBlur={validationBlur}
                labelWidth="150px"
                required
              />
              <InputWithHorizontalLabel
                name="assignment"
                placeholder={intl.formatMessage({
                  id: "assignment",
                })}
                label={intl.formatMessage({
                  id: "assignment",
                })}
                description={intl.formatMessage(
                  {id: "up_to_custom_characters"},
                  {quantity: "50"},
                )}
                validationBlur={validationBlur}
                labelWidth="150px"
                maxLength="50"
                required
              />
              <InputWithHorizontalLabel
                name="email"
                placeholder="E-mail"
                label="E-mail"
                validationBlur={validationBlur}
                labelWidth="150px"
                required={!isFastTrack}
              />
              {personType === "I" ? (
                <>
                  <SelectWithLabel
                    name="country"
                    label={intl.formatMessage({
                      id: "country",
                    })}
                    placeholder={intl.formatMessage({
                      id: "select_country_where_live",
                    })}
                    labelOrientation="horizontal"
                    labelWidth="150px"
                    options={countries}
                    validationBlur={validationBlur}
                    isSearchable={true}
                    required
                  />
                  <InputWithHorizontalLabel
                    name="internationalCity"
                    placeholder={intl.formatMessage({
                      id: "type_city_where_live",
                    })}
                    label={intl.formatMessage({
                      id: "city",
                    })}
                    validationBlur={validationBlur}
                    labelWidth="150px"
                    maxLength="255"
                    required
                  />
                </>
              ) : (
                <>
                  <SelectWithLabel
                    name="state"
                    placeholder={intl.formatMessage({
                      id: "select_state_where_live",
                    })}
                    label={intl.formatMessage({
                      id: "state",
                    })}
                    labelOrientation="horizontal"
                    labelWidth="150px"
                    options={brazilianStates}
                    validationBlur={validationBlur}
                    isSearchable={true}
                    onChange={handleStateChange}
                    required
                  />
                  <SelectWithLabel
                    name="brazilianCity"
                    placeholder={intl.formatMessage({
                      id: "select_city_where_live",
                    })}
                    label={intl.formatMessage({
                      id: "city",
                    })}
                    labelOrientation="horizontal"
                    labelWidth="150px"
                    options={brazilianCities}
                    validationBlur={validationBlur}
                    isSearchable={true}
                    required
                  />
                </>
              )}
              <TelephoneInputWithLabel
                name="telephone"
                placeholder={intl.formatMessage({
                  id: "telephone",
                })}
                label={intl.formatMessage({
                  id: "telephone",
                })}
                validationBlur={validationBlur}
                labelOrientation="horizontal"
                labelWidth="150px"
                required
              />
              <Toggle
                name="create_credential"
                label={intl.formatMessage({
                  id: "create_credential_collaborator_page",
                })}
                onClick={() => setIsCreateCredential(!isCreateCredential)}
              />
            </FieldsContainer>
            {isCreateCredential && (
              <FieldsContainer>
                <SelectWithLabel
                  name="event"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "event_edition" })}
                  placeholder={intl.formatMessage({ id: "event_edition" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  options={events}
                  onChange={(event) => setSelectedEvent(event?.value)}
                  required
                />
                <SelectWithLabel
                  name="opportunity"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "contract" })}
                  placeholder={intl.formatMessage({ id: "contract" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  options={opportunities}
                  onChange={(opportunity) =>
                    setSelectedOpportunity(opportunity?.value)
                  }
                  required
                />
                <SelectWithLabel
                  name="category"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "credential_types" })}
                  placeholder={intl.formatMessage({ id: "credential_types" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  options={credentialTypes}
                  required
                />
              </FieldsContainer>
            )}
          </Session>
        </Form>
      </Container>
      <ConfirmationModal
        setIsOpen={setIsModalOpen}
        modalIsOpen={isModalOpen}
        confirmationFunction={resetForm}
        cancelationFunction={() =>
          history.go(-2)
        }
        description={intl.formatMessage({
          id: "new_collaborator_confirmation",
        })}
        confirmationText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
      />
    </AnimatedMainContainer>
  );
};

export default AdminAssociateCollaboratorsCreate;
